import { BalloonEditor } from "@ckeditor/ckeditor5-editor-balloon";
import { Alignment } from '@ckeditor/ckeditor5-alignment';
import { Autoformat } from '@ckeditor/ckeditor5-autoformat';
import {
	Bold,
	Italic,
	Strikethrough,
	Subscript,
	Superscript,
	Underline
} from '@ckeditor/ckeditor5-basic-styles';
import { BlockQuote } from '@ckeditor/ckeditor5-block-quote';
import { Essentials } from '@ckeditor/ckeditor5-essentials';
import { FontBackgroundColor, FontColor, FontFamily, FontSize } from '@ckeditor/ckeditor5-font';
import { Heading } from '@ckeditor/ckeditor5-heading';
import { Highlight } from '@ckeditor/ckeditor5-highlight';
import { HorizontalLine } from '@ckeditor/ckeditor5-horizontal-line';
import { HtmlEmbed } from '@ckeditor/ckeditor5-html-embed';
import { GeneralHtmlSupport } from '@ckeditor/ckeditor5-html-support';
import {
	AutoImage,
	Image,
	ImageCaption,
	ImageInsert,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	ImageUpload
} from '@ckeditor/ckeditor5-image';
import {
	AutoVideo,
	Video,
	VideoInsert,
	VideoResize,
	VideoStyle,
	VideoToolbar,
	VideoUpload
} from "../plugins/ckeditor5-video/index";
import { Indent, IndentBlock } from '@ckeditor/ckeditor5-indent';
import { AutoLink, Link, LinkImage } from '@ckeditor/ckeditor5-link';
import { List, ListProperties, TodoList } from '@ckeditor/ckeditor5-list';
import { MediaEmbed, MediaEmbedToolbar } from '@ckeditor/ckeditor5-media-embed';
import { MediaStyle, MediaResize } from '../plugins/ckeditor5-media-embed';
import { Mention } from '@ckeditor/ckeditor5-mention';
import { Paragraph } from '@ckeditor/ckeditor5-paragraph';
import { PasteFromOffice } from '@ckeditor/ckeditor5-paste-from-office';
import { RemoveFormat } from '@ckeditor/ckeditor5-remove-format';
import {
	SpecialCharacters,
	SpecialCharactersArrows,
	SpecialCharactersCurrency,
	SpecialCharactersEssentials,
	SpecialCharactersLatin,
	SpecialCharactersMathematical,
	SpecialCharactersText
} from '@ckeditor/ckeditor5-special-characters';
import {
	Table,
	TableCaption,
	TableColumnResize,
	TableProperties,
	TableToolbar
} from '@ckeditor/ckeditor5-table';
import { TableCellProperties } from '../plugins/ckeditor5-table/index';
import { TextTransformation } from '@ckeditor/ckeditor5-typing';
import { BlockToolbar } from '@ckeditor/ckeditor5-ui';
import { WordCount } from '@ckeditor/ckeditor5-word-count';
// Plugins de weknow
import { Accordion } from '../plugins/ckeditor5-accordion/index';
import { UploadAdapterPlugin } from '../plugins/ckeditor5-upload/index';
import { FileUpload } from "../plugins/ckeditor5-fileupload/index";
import { AudioUpload } from "../plugins/ckeditor5-audio/index";
import { Emojis } from "../plugins/ckeditor5-emojis/index";

// import { AIAssistant } from "../plugins/ckeditor5-ai/index";

class Editor extends BalloonEditor {
    public static override builtinPlugins = [
        Accordion,
		// AIAssistant,
		Alignment,
		AutoImage,
		AutoLink,
		Autoformat,
		BlockQuote,
		BlockToolbar,
		Bold,
		Emojis,
		Essentials,
		FileUpload,
		AudioUpload,
		FontBackgroundColor,
		FontColor,
		FontFamily,
		FontSize,
		GeneralHtmlSupport,
		Heading,
		Highlight,
		HorizontalLine,
		HtmlEmbed,
		Image,
		ImageCaption,
		ImageInsert,
		ImageResize,
		ImageStyle,
		ImageToolbar,
		ImageUpload,
		Indent,
		IndentBlock,
		Italic,
		Link,
		LinkImage,
		List,
		ListProperties,
		MediaEmbed,
		MediaEmbedToolbar,
		MediaStyle,
		MediaResize,
		Mention,
		Paragraph,
		PasteFromOffice,
		RemoveFormat,
		SpecialCharacters,
		SpecialCharactersArrows,
		SpecialCharactersCurrency,
		SpecialCharactersEssentials,
		SpecialCharactersLatin,
		SpecialCharactersMathematical,
		SpecialCharactersText,
		Strikethrough,
		Subscript,
		Superscript,
		Table,
		TableCaption,
		TableCellProperties,
		TableColumnResize,
		TableProperties,
		TableToolbar,
		TextTransformation,
		TodoList,
		Underline,
		WordCount,
		AutoVideo,
		Video,
		VideoInsert,
		VideoResize,
		VideoStyle,
		VideoToolbar,
		VideoUpload,
		UploadAdapterPlugin,
	];
    public static override defaultConfig = {
		// Provide the license key.
		toolbar: {
			items: [
				// 'aiAssistant',
				'|',
				'heading',
				'fontFamily',
				'fontSize',
				'fontColor',
				'fontBackgroundColor',
				'|',
				'bold',
				'italic',
				'underline',
				'strikethrough',
				'alignment',
				'highlight',
				'|',
				'link',
				'|',
				'subscript',
				'superscript',
				'|',
				'removeFormat'
			]
		},
		language: 'es',
		blockToolbar: [
			// 'aiCommands',
			'|',
			'bulletedList',
			'numberedList',
			'indent',
			'outdent',
			'|',
			'insertTable',
			'imageInsert',
			'videoUpload',
			'audioUpload',
			'mediaEmbed',
			'fileUpload',
			'htmlEmbed',
			'insertAccordion',
			'blockQuote',
			'horizontalLine',
			'emojisButton',
			'specialCharacters',
			'todoList',
			'|',
			'undo',
			'redo'
		],
		image: {
			resizeOptions: [
				{
					name: 'resizeImage:original',
					value: null,
					icon: 'original'
				},
				{
					name: 'resizeImage:100',
					value: '100',
					icon: 'large'
				},
				{
					name: 'resizeImage:75',
					value: '75',
					icon: 'large'
				},
				{
					name: 'resizeImage:50',
					value: '50',
					icon: 'medium'
				},
				{
					name: 'resizeImage:25',
					value: '25',
					icon: 'small'
				}
			],
			toolbar: [
				'imageTextAlternative',
				'toggleImageCaption',
				'imageStyle:inline',
				'imageStyle:block',
				'imageStyle:side',
				'|',
				'linkImage',
				'|',
				'resizeImage'
			]
		},
		video: {
			resizeUnit: '%',
            resizeOptions: [
				{
                    name: 'resizeVideo:original',
                    value: null,
                    icon: 'original'
                },
                {
                    name: 'resizeVideo:25',
                    value: '25',
                    icon: 'small'
                },
                {
                    name: 'resizeVideo:50',
                    value: '50',
                    icon: 'medium'
                },
                {
                    name: 'resizeVideo:75',
                    value: '75',
                    icon: 'large'
                },
				{
                    name: 'resizeVideo:100',
                    value: '100',
                    icon: 'large'
                }
			],
			toolbar: [
				'videoStyle:inline',
				'videoStyle:block',
				'videoStyle:side',
				'|',
				'videoResize'
			]
		},
		table: {
			contentToolbar: [
				'tableColumn',
				'tableRow',
				'mergeTableCells',
				'tableCellProperties',
				'tableProperties'
			]
		},
		mediaEmbed: {
			toolbar: [ 'mediaStyle:side-left', 'mediaStyle:full', 'mediaStyle:side-right', '|', 'mediaResize' ],
			removeProviders: [ 'instagram', 'twitter', 'googleMaps', 'flickr', 'facebook' ],
			extraProviders: [
				{
					name: 'odas',
					url: /^odas.we-know\.net\/(\w+).+\.mp4(\?.*)?$/,
					html: (match: any | any[]) => {
                        const input = match['input'];
                        return ('<div style="position: relative;height: 35.2rem!important; ">' +
                            `<iframe src="https://${input}" ` +
                            ' width="100%" height="340px" style="max-width:  100%;"' +
                            ' allowfullscreen="allowfullscreen" mozallowfullscreen="mozallowfullscreen"' +
							' msallowfullscreen="msallowfullscreen" oallowfullscreen="oallowfullscreen"' +
							' webkitallowfullscreen="webkitallowfullscreen">' +
                            '</iframe>' +
                            '</div>');
                    }
				}
			],
		}
	};
}
export default Editor;
